import React, { useContext, useEffect, useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { getManualRuleResultsEndPoint } from '../../../common/api.endPoints';
import { Loading } from '../../../common/components/Loading';
import { FlexGrid, Spacer, TitleMedium } from '../../../common/components/v2';
import { Resource } from '../../../common/constants/resource';
import { HttpService } from '../../../common/http.service';
import { SnackbarContext } from '../../layout/SnackbarContext';
import {
  DECISION_TASK_DEFAULT_ID,
  getAllTasks,
} from '../../tasks/slice/taskListSlice';
import { fetchTasks, resetApplication } from '../../tasks/slice/taskThunks';
import { TaskTypes } from '../../tasks/types/taskTypes';
import { Decision } from '../decision/Decision';
import { fetchApplication } from '../detailSlice';
import { RuleResultsView } from '../ruleResults/RuleResultsView';
import styles from '../styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const RuleResultsDetails = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  if (applicationId === undefined) {
    return <Navigate to={{ pathname: '/' }} />;
  }

  const dispatch = useDispatch<AppDispatch>();
  const [applicationFetchStatus, setApplicationFetchStatus] = useState('idle');
  const [taskFetchStatus, setTaskFetchStatus] = useState('idle');
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const [ruleResultResp, setRuleResultResp] = useState<any>(null);
  const [noEvidenceToRunError, setNoEvidenceToRunError] = useState(false);
  const [ruleResultsResponseReady, setRuleResultsResponseReady] =
    useState(false);
  const decisionTask = useSelector(state =>
    getAllTasks(state.taskList).find(t => t.taskType === TaskTypes.DECISION),
  );

  const applicationReady =
    applicationFetchStatus === 'success' && taskFetchStatus === 'success';
  const updateSnacks = (updatedSnacks: any) => {
    setSnacks(updatedSnacks);
  };

  const policyNumber = useSelector(
    state => state.application.carrierPolicyNumber,
  );
  const product = useSelector(state => state.application.product);
  const carrier = useSelector(state => state.application.carrier);
  const tasks = useSelector(state => state.taskList.entities);

  useEffect(() => {
    document.title = `${policyNumber}${Resource.NORA_M_DASH} Medical Claims Rule Results`;
  });

  useEffect(() => {
    (async () => {
      dispatch(resetApplication());
      dispatch(
        fetchApplication(
          applicationId,
          setApplicationFetchStatus,
          openSnacks,
          updateSnacks,
        ),
      );
      dispatch(
        fetchTasks(applicationId, setTaskFetchStatus, openSnacks, updateSnacks),
      );
    })();
  }, []);

  useEffect(() => {
    if (!applicationReady) {
      return;
    }
    const hasMedClaimsTask = Object.values(tasks).some(t => {
      return (
        t?.taskType === TaskTypes.AUTO_MEDICAL_CLAIMS ||
        t?.taskType === TaskTypes.MEDICAL_CLAIMS
      );
    });
    if (!hasMedClaimsTask) {
      setNoEvidenceToRunError(true);
      return;
    }
    (async () => {
      try {
        const response = await HttpService.getInstance().post(
          getManualRuleResultsEndPoint(applicationId),
        );

        if (response?.error) {
          setRuleResultResp({
            error: response?.error?.message || 'Internal error',
          });
          return;
        }
        setRuleResultResp(response.parsedBody);
      } catch (err) {
        setRuleResultResp({
          error: 'Internal error',
        });
      } finally {
        setRuleResultsResponseReady(true);
      }
    })();
  }, [applicationReady]);

  const buildRuleResultsData = (ruleResultsResp: any) => {
    if (!ruleResultsResp?.ruleSets) {
      return {
        ruleResults: null,
        fetchRuleResultsInProgress: false,
        fetchRuleResultsError: true,
      };
    }
    return {
      ruleResults: ruleResultsResp,
      fetchRuleResultsInProgress: false,
      fetchRuleResultsError: false,
    };
  };

  if (noEvidenceToRunError) {
    return (
      <>
        <div className={styles.Wrap}>
          <div className={styles.Main}>
            <div className={styles.Centered}>
              <FlexGrid>
                <TitleMedium>
                  This application does not have medical claims evidence to run
                  (-_-;)
                </TitleMedium>
              </FlexGrid>
              <Spacer height={24} />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!applicationReady || !ruleResultsResponseReady) {
    return (
      <>
        <Loading fullscreen={true} />
      </>
    );
  }

  if (
    ruleResultResp?.error ||
    !ruleResultResp?.decision ||
    !ruleResultResp?.ruleSets
  ) {
    return (
      <>
        <div className={styles.Wrap}>
          <div className={styles.Main}>
            <div className={styles.Centered}>
              <FlexGrid>
                <TitleMedium>
                  Something bad happened. Please inform the engineers (._. )(
                  ._.)
                </TitleMedium>
                <Spacer height={24} />
                <Box>
                  <pre>{JSON.stringify(ruleResultResp, null, 2)}</pre>
                </Box>
              </FlexGrid>
              <Spacer height={24} />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.Wrap}>
        <div className={styles.Main}>
          <div className={styles.Centered}>
            <FlexGrid>
              <TitleMedium>
                {policyNumber + ' Medical Claims Rule Results'}
              </TitleMedium>
              <Decision
                decisions={[ruleResultResp?.decision]}
                product={product}
                carrier={carrier}
                decisionTask={decisionTask}
                title={'Rule results outcome'}
                key={DECISION_TASK_DEFAULT_ID}
              />
              <Spacer height={24} />
              <RuleResultsView
                data={buildRuleResultsData(ruleResultResp)}
                isRidersTab={false}
                isGen2={true}
              />
            </FlexGrid>
            <Spacer height={24} />
          </div>
        </div>
      </div>
    </>
  );
};
